import {
    LightComponent,
    MeshComponent,
    Object3D,
    PhysXComponent,
    TextComponent,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {CursorTarget} from '@wonderlandengine/components';
import {ActiveCamera} from './active-camera.js';
import {InspectWorldObject} from './inspect-world-object.js';
import {vec3} from 'gl-matrix';
import {Blink} from './blink.js';
import {WasdControlsFps} from './wasd-controls-fps.js';
import {delay} from '../utils/delay.js';
import {LevelManagerScene2} from '../scene2/level-manager-scene2.js';
import {Teleport} from './teleport.js';
import {Inspect} from './inspect.js';

/**
 * inspect-note
 */
export class InspectNote extends Inspect {
    static TypeName = 'inspect-note';
    static InheritProperties = true;

    /* Properties that are configurable in the editor */
    @property.object()
    inspectSphere?: Object3D; /* The sphere that will be closed on us, exists in Player's child */
    @property.object()
    inspectLight?: Object3D;
    @property.float(0)
    rotOffsetY = 0; /* Offset Y rotation of the inspected object */

    @property.float(0.8)
    delayBeforeText = 0.8; /* Delay time before Text Writing starts */

    /**
     * The textContentObject which will Retrieve Text from, to write it letter by letter
     */
    @property.object()
    textObject?: Object3D;
    //textDisplay: Property.string(), //changed to store it from textObject instead.
    //previousTextDisplay: Property.string(), /* If there's a previous text display that we want to continue writing upon */
    @property.object()
    strikeThroughTexts?: Object3D; /* If there's a static text that needs to be striken-through, then reference the strikethrough text */
    @property.float(0.7)
    delayBeforeStrikeThrough = 0.7; /* Delay before striking through (If there's strikeThroughTexts) */
    @property.float(70)
    delayLetters = 70;
    @property.object()
    levelManager?: Object3D; /* Level Manager to Send message to (optional) */
    @property.string()
    msgToLvlManager = ''; /* Send a message to level-manager on End/Reset (optional) */

    private delayLettersDefault = 0;
    private textAppeared = false;
    private noteGate = false;
    private playerTransform = vec3.create();
    private noteObjTransform = vec3.create();
    private textDisplay = '';
    private indexLetter = 0;
    private blink?: Blink;
    private playerPhysXComponent?: PhysXComponent;
    private playerWasdControlsFps?: WasdControlsFps;
    private inspectSphereMesh?: MeshComponent;
    private inspectLightLight?: LightComponent;
    private textComponent?: TextComponent;
    private teleport?: Teleport;

    start() {
        /* Add Cursor Functions */
        const target =
            this.object.getComponent(CursorTarget) ||
            this.object.addComponent(CursorTarget)!;

        target.onDown.add(this.onDown.bind(this));

        /* General */
        const activeCameraComponent = this.player.getComponent(ActiveCamera);
        if (!activeCameraComponent) {
            throw new Error(
                `inspect-note(${this.object.name}): player does not have an ActiveCamera component`
            );
        }

        const tempTeleport = this.player.getComponent(Teleport);
        if (!tempTeleport) {
            throw new Error(
                `inspect-world-object(${this.object.name}): player does not have a Teleport component`
            );
        }
        this.teleport = tempTeleport;

        const tempBlink = this.player.getComponent(Blink);
        if (!tempBlink) {
            throw new Error(
                `inspect-note(${this.object.name}): player does not have a Blink component`
            );
        }
        this.blink = tempBlink;

        const tempPhysxComponent = this.player.getComponent(PhysXComponent);
        if (!tempPhysxComponent) {
            throw new Error(
                `inspect-note(${this.object.name}): player does not have a PhysXComponent component`
            );
        }
        this.playerPhysXComponent = tempPhysxComponent;

        const tempWasdControlsFps = this.player.getComponent(WasdControlsFps);
        if (!tempWasdControlsFps) {
            throw new Error(
                `inspect-note(${this.object.name}): player does not have a WasdControlsFps component`
            );
        }
        this.playerWasdControlsFps = tempWasdControlsFps;

        if (!this.inspectSphere) {
            throw new Error(
                `inspect-note(${this.object.name}): inspectSphere is not defined`
            );
        }

        if (!this.inspectLight) {
            throw new Error(
                `inspect-note(${this.object.name}): inspectLight is not defined`
            );
        }

        this.delayLettersDefault = this.delayLetters;

        const tempInspectSphereMesh = this.inspectSphere.getComponent(MeshComponent);
        if (!tempInspectSphereMesh) {
            throw new Error(
                `inspect-note(${this.object.name}): inspectSphere does not have a MeshComponent component`
            );
        }
        this.inspectSphereMesh = tempInspectSphereMesh;

        const tempInspectLightLight = this.inspectLight.getComponent(LightComponent);
        if (!tempInspectLightLight) {
            throw new Error(
                `inspect-note(${this.object.name}): inspectLight does not have a LightComponent component`
            );
        }
        this.inspectLightLight = tempInspectLightLight;

        if (!this.textObject) {
            throw new Error(`inspect-note(${this.object.name}): textObject is not defined`);
        }
        const tempTextComponent = this.textObject.getComponent(TextComponent);
        if (!tempTextComponent) {
            throw new Error(
                `inspect-note(${this.object.name}): textObject does not have a TextComponent component`
            );
        }
        this.textComponent = tempTextComponent;

        /* TextLetters */
        this.resetLetters();
    }

    onDown() {
        /* If Still writing, then finish Writing, else Reset */
        if (this.textAppeared == false) {
            this.delayLetters = 0;
        } else {
            this.delayLetters = this.delayLettersDefault;
            this.resetSelf();
        }
    }

    startNote(inspectedObj: Object3D | null = null, skipTeleport = false) {
        /* Gate */
        this.noteGate = true;

        /* Store Transforms (Only if not called from inspectedObject/"inspect-world-object") */
        if (inspectedObj == null) {
            this.noteObjTransform = this.object.getTransformWorld();
        } else {
            const inspectWorldObjectComponent =
                inspectedObj.getComponent(InspectWorldObject);
            if (!inspectWorldObjectComponent) {
                throw new Error(
                    `inspect-note(${this.object.name}): inspectedObj does not have an InspectWorldObject component`
                );
            }
            /* Get them from inspectedObject */
            this.noteObjTransform = inspectWorldObjectComponent.noteObjTransform;
        }

        /* Blink & Change Locations */
        this.blink!.startBlink(
            () => {
                this.playerPhysXComponent!.kinematic = true; // Turn to Kinematic to be able to change Physx location

                if (!skipTeleport) {
                    this.teleport!.teleport([30, 0, 0]);
                }

                this.playerWasdControlsFps!.active = false; /* Disable Movement */

                /* Inspected Sphere & Light, Activate & Set Locations */
                this.inspectSphereMesh!.active = true;
                this.inspectLightLight!.active = true;
                /* Inspected Light Transform */
                this.setTransformInfrontOf(
                    this.player,
                    this.inspectLight!,
                    5
                );

                /* Note Transform */
                this.setTransformInfrontOf(
                    this.player,
                    this.object,
                    this.engine.xr ? 1.8 : 0.7,
                    0,
                    [0, this.rotOffsetY, 0]);
            },
            async () => {
                /* Strikethrough */
                if (this.strikeThroughTexts) {
                    await this.strikeThrough();
                }

                /* TextLetters */
                await delay(
                    this.delayBeforeText
                ); /* Delay time before Text Writing starts */
                this.displayNextLetter();
            }
        ).catch(console.error);
    }

    async strikeThrough() {
        if (this.strikeThroughTexts) {
            await delay(this.delayBeforeStrikeThrough);
            const textComponent = this.strikeThroughTexts.getComponent(TextComponent);
            if (textComponent) {
                textComponent.active = true;
            } else {
                console.warn(
                    `inspect-note(${this.object.name}): strikeThroughTexts does not have a TextComponent`
                );
            }
        }
    }

    resetLetters() {
        this.textAppeared = false;
        this.textDisplay = this.textComponent!.text;
        this.textComponent!.text = '';
        this.indexLetter = this.textComponent!.text.length;

        if (this.strikeThroughTexts) {
            const strikeThroughTextComponent =
                this.strikeThroughTexts.getComponent(TextComponent);
            if (!strikeThroughTextComponent) {
                console.error(
                    `inspect-note(${this.object.name}): strikeThroughTexts does not have a TextComponent`
                );
                return;
            }
            strikeThroughTextComponent.active = false;
        }
    }

    displayNextLetter() {
        /* Text Appear Letter by Letter */
        // if (this.textAppeared) {
        //     this.resetLetters();
        // }
        if (this.indexLetter < this.textDisplay.length) {
            this.textAppeared = false;
            this.textComponent!.text += this.textDisplay.charAt(
                this.indexLetter
            ); /* Add the next letter to the container */
            this.indexLetter++; /* Move to the next letter */
            setTimeout(
                () => this.displayNextLetter(),
                this.delayLetters
            ); /* Call this function again after the delay */
        } else {
            this.textAppeared = true;
        }
    }

    resetSelf() {
        /* Blink & Change Locations */
        this.blink!.startBlink(() => {
            this.teleport!.back();
            //this.player!.setTransformWorld(this.playerTransform);
            this.object.setTransformWorld(this.noteObjTransform);

            /* Disable InspectSphere */
            this.inspectSphereMesh!.active = false;
            this.inspectLightLight!.active = false;

            /* Re-enable Movement */
            //await delay(0.1); // Gives time for kinematic to function
            this.playerPhysXComponent!.kinematic = false;
            this.playerWasdControlsFps!.active = true;

            /* Send Message to Level Manager on End */
            if (this.levelManager != null && this.msgToLvlManager != '') {
                const levelManagerComponent =
                    this.levelManager.getComponent(LevelManagerScene2);
                if (!levelManagerComponent) {
                    console.error(
                        `inspect-note(${this.object.name}): levelManager does not have a LevelManagerScene2 component`
                    );
                    return this;
                }
                levelManagerComponent.sendMessage(this.msgToLvlManager);
            }
        }).catch(console.error);
        return this;
    }
}
